<template>
  <ul class="context-menu">
    <li
      v-for="(item, index) in visibleItems"
      :key="index"
      class="context-menu-item"
      :class="{
       disabled:        reasonDisabled(item) !== null,
     'divider-after': item.dividerAfter,
    }"
      @mouseenter="$core.tooltip.show($event.currentTarget, reasonDisabled(item))"
      @mouseleave="$core.tooltip.hide()"
      @click="onItemClick(item)"
    >
      <VIcon
        class="context-menu-icon"
        :material="item.icon"
        theme="outlined"
      />
      <span class="context-menu-title">{{ resolveTitle(item) }}</span>
    </li>

    <li
      v-if="showSelectOption"
      class="context-menu-item divider-after"
      @click="toggleSelect"
    >
      <VIcon
        class="context-menu-icon"
        :material="selectable ? 'cancel' : 'add'"
        theme="outlined"
      />

      <span class="context-menu-title"> {{selectable ? 'Отменить выбор' : 'Выбрать'}} </span>
    </li>
  </ul>
</template>

<style lang="scss" src="./VContextMenu.scss"/>

<script lang="ts">

import type ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

export default defineComponent({
  name: 'VContextMenu',

  props: {
    items: {
      type:     Array as PropType<ContextMenuItem[]>,
      required: true,
    },
    row: {
      type:     Object as PropType<Record<string, unknown>>,
      required: true,
    },
    /**
     * Колбэк «обнови меня», который грид прокидывает внутрь.
     * Зовётся после callback пункта, если refreshAfter !== false.
     */
    onAction: {
      type:    Function as PropType<() => void>,
      default: null,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    onRowSelect: {
      type:    Function as PropType<() => void>,
      default: null,
    },
    cancelSelection: {
      type:    Function as PropType<() => void>,
      default: null,
    },

    /** Показывать пункт "Выбрать" в таблице */
    showSelectOption: {
      type: Boolean,
      default: false,
    },

    /**
     * Показывать ли пункт "Открыть". По умолчанию true. Если false —
     * пункт с title === 'Открыть' вырезается из меню. Используется
     * там, где открытие карточки происходит другим способом и
     * дублирующий пункт не нужен (например, внутри уже открытой карточки).
     */
    showOpenOption: {
      type: Boolean,
      default: true,
    },
  },

  /**
   * contextMenuItem:done — пункт меню выполнен. Летит по любому пункту,
   * дошедшему до конца без исключения. Нагрузка — сам пункт, строка и
   * результат callback:
   * решение «реагировать или нет» (обычно по refreshAfter) принимает
   * слушатель снаружи, здесь ничего не фильтруется.
   */
  emits: ['contextMenuItem:done'],

  computed: {
    /**
     * Пункты, прошедшие проверку hidden и showOpenOption.
     * Если showOpenOption === false — находим пункт "Открыть" по
     * разрешённому title (учитывая, что title может быть функцией)
     * и удаляем его из коллекции.
     */
    visibleItems(): ContextMenuItem[] {
      const filtered = this.items.filter(item => this.isHidden(item) === false);

      if (this.showOpenOption === false) {
        const openIndex = filtered.findIndex(item => this.resolveTitle(item) === 'Открыть');
        if (openIndex !== -1) {
          filtered.splice(openIndex, 1);
        }
      }

      return filtered;
    },
  },

  methods: {
    /**
     * Возвращает подпись пункта. title может быть функцией от строки
     * (например, чтобы менять текст «Сделать ставку» / «Изменить ставку»
     * в зависимости от данных строки) либо плоской строкой.
     */
    resolveTitle(item: ContextMenuItem): string {
      if (typeof item.title === 'function') {
        return item.title(this.row);
      } else {
        return item.title;
      }
    },

    /**
     * Вычисляет, скрыт ли пункт. hidden может быть функцией от строки
     * либо плоским boolean.
     */
    isHidden(item: ContextMenuItem): boolean {
      if (typeof item.hidden === 'function') {
        return item.hidden(this.row);
      } else {
        return item.hidden;
      }
    },

    /**
     * Возвращает причину недоступности пункта (строку) либо null.
     * disabled может быть функцией от строки либо плоской строкой.
     * Строка-причина пока не выводится (тултип сделает отдельно),
     * но используется как флаг недоступности.
     */
    reasonDisabled(item: ContextMenuItem): string | null {
      if (typeof item.disabled === 'function') {
        return item.disabled(this.row);
      } else {
        return item.disabled || null;
      }
    },

    async onItemClick(item: ContextMenuItem): Promise<void> {
      if (this.reasonDisabled(item) !== null) {
        return;
      }

      /**
       * Отдаём своё положение попапам:
       * Есть ситуации, когда из контекстного меню открывается попап. Вместо якоря
       * мы запоминаем элемент контекстное меню и попап сам возьмет координаты левого верхнего угла
       * контекстного меню, и встанет на это место
       */
      this.$core.utils.dom.setContextMenuCoords(this.$el);

      /** Значение, которым завершился callback. Нужно слушателю для closeDialogOnSuccess */
      let result: unknown;

      try {
        result = await item.callback(this.row);
      } catch (error) {
        this.$core.logException(error);
        // Действие упало — состояние не изменилось, рефрешить нечего. Выходим.
        return;
      }

      this.$emit('contextMenuItem:done', item, this.row, result)

      if (item.refreshAfter === true && this.onAction) {
        this.onAction();
      }
    },

    toggleSelect() {
      if (this.selectable === true) this.cancelSelection();
      else this.onRowSelect(this.row);
    },
  },
});
</script>